// Permits hub page — Phase C-PERM v1.
// URL: /permits
// Layout: header + KPIs + filter chips + (map + list) + detail drawer
// Modeled after the As-Built page architecture.

import { useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import PermitsMap from '../components/PermitsMap';
import PermitDetailDrawer from '../components/PermitDetailDrawer';
import AddPermitModal from '../components/AddPermitModal';

type Project = {
  id: string;
  project_code: string;
  project_name: string;
  county?: string;
  state?: string;
  prime_contractor?: string;
};

export type Permit = {
  id: string;
  project_id: string;
  kind: string;
  bucket: string;
  status: string;
  jurisdiction: string | null;
  agency_id: string | null;
  agency_name: string | null;
  agency_short_name: string | null;
  agency_kind: string | null;
  permit_number: string | null;
  application_ref: string | null;
  needed_at: string | null;
  applied_at: string | null;
  issued_at: string | null;
  expires_at: string | null;
  feature_name: string | null;
  feature_owner: string | null;
  notes: string | null;
  metadata: Record<string, any>;
  segment_id: string | null;
  pole_id: string | null;
  structure_id: string | null;
  project_only: boolean;
  geom: any;
  is_sample: boolean;
};

type ViewMode = 'split' | 'map' | 'list';

// Bucket → label + color
const BUCKET_META: Record<string, { label: string; color: string; icon: string }> = {
  environmental: { label: 'Environmental',  color: '#10b981', icon: '🌿' },
  crossing:      { label: 'Crossings',      color: '#f59e0b', icon: '🚆' },
  row:           { label: 'Right-of-Way',   color: '#3b82f6', icon: '🛣' },
  pole:          { label: 'Poles',          color: '#8b5cf6', icon: '⚡' },
  site:          { label: 'Sites',          color: '#1e293b', icon: '🏢' },
  other:         { label: 'Other',          color: '#64748b', icon: '◆' },
};

const KIND_LABELS: Record<string, string> = {
  nepa: 'NEPA',
  section_404: '§404 (Clean Water Act)',
  section_106: '§106 (Historic Preservation)',
  tribal_consultation: 'Tribal Consultation',
  esa_section_7: 'ESA §7',
  floodplain: 'Floodplain',
  mbta: 'Migratory Bird Treaty',
  railroad: 'Railroad Crossing',
  dot_access: 'DOT Access',
  highway: 'Highway Crossing',
  row_county: 'County ROW',
  row_city: 'City ROW',
  pole_attach: 'Pole Attachment',
  make_ready: 'Make-Ready',
  building: 'Building Permit',
  other: 'Other',
};

const STATUS_META: Record<string, { label: string; bg: string; fg: string }> = {
  needed:     { label: 'Needed',     bg: '#fee2e2', fg: '#991b1b' },
  applied:    { label: 'Applied',    bg: '#fef3c7', fg: '#92400e' },
  in_review:  { label: 'In review',  bg: '#dbeafe', fg: '#1e40af' },
  approved:   { label: 'Approved',   bg: '#d1fae5', fg: '#065f46' },
  issued:     { label: 'Issued',     bg: '#dcfce7', fg: '#065f46' },
  expired:    { label: 'Expired',    bg: '#fecaca', fg: '#7f1d1d' },
  revoked:    { label: 'Revoked',    bg: '#fecaca', fg: '#7f1d1d' },
  superseded: { label: 'Superseded', bg: '#f1f5f9', fg: '#475569' },
  denied:     { label: 'Denied',     bg: '#fecaca', fg: '#7f1d1d' },
  waived:     { label: 'Waived',     bg: '#e5e7eb', fg: '#6b7280' },
  renewing:   { label: 'Renewing',   bg: '#fef3c7', fg: '#92400e' },
  n_a:        { label: 'N/A',        bg: '#e5e7eb', fg: '#6b7280' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_META[status] ?? STATUS_META.needed;
  return (
    <span style={{
      background: s.bg, color: s.fg,
      padding: '2px 8px', borderRadius: 999,
      fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '.04em',
      whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

export default function PermitsPage() {
  const [projectId, setProjectId] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [selectedPermit, setSelectedPermit] = useState<Permit | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [bucketFilter, setBucketFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const qc = useQueryClient();

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['bead-projects-permits'],
    queryFn: () => api.get('/bead/projects'),
  });

  // Auto-select the first real project once the list loads (no stale hardcoded id).
  useEffect(() => {
    if (!projectId && projects.length > 0) setProjectId(projects[0].id);
  }, [projects, projectId]);

  const project = projects.find(p => p.id === projectId);
  const selectableProjects = projects;

  const { data: permitsData, refetch } = useQuery<{ items: Permit[] }>({
    queryKey: ['permits', projectId, bucketFilter, statusFilter],
    queryFn: () => {
      const qs = new URLSearchParams({ project_id: projectId });
      if (bucketFilter) qs.set('bucket', bucketFilter);
      if (statusFilter) qs.set('status', statusFilter);
      return api.get(`/permits?${qs.toString()}`);
    },
    enabled: !!projectId,
  });
  const permits = permitsData?.items ?? [];

  // Compute KPIs across the UNFILTERED set (need a second query for that)
  const { data: allPermits } = useQuery<{ items: Permit[] }>({
    queryKey: ['permits-all', projectId],
    queryFn: () => api.get(`/permits?project_id=${projectId}`),
    enabled: !!projectId,
  });
  const all = allPermits?.items ?? [];

  const kpis = useMemo(() => {
    const byStatus: Record<string, number> = {};
    const byBucket: Record<string, number> = {};
    let expiringIn30 = 0;
    const now = new Date();
    const in30 = new Date(now.getTime() + 30 * 86400 * 1000);
    for (const p of all) {
      byStatus[p.status] = (byStatus[p.status] ?? 0) + 1;
      byBucket[p.bucket] = (byBucket[p.bucket] ?? 0) + 1;
      if (p.expires_at) {
        const exp = new Date(p.expires_at);
        if (exp <= in30 && exp >= now) expiringIn30++;
      }
    }
    const inFlight = (byStatus.applied ?? 0) + (byStatus.in_review ?? 0);
    const issued = (byStatus.issued ?? 0) + (byStatus.approved ?? 0);
    return {
      total: all.length,
      needed: byStatus.needed ?? 0,
      inFlight,
      issued,
      denied: byStatus.denied ?? 0,
      expiringIn30,
      byStatus,
      byBucket,
    };
  }, [all]);

  // Auto-detect mutation — "Detect permits now" button
  const detectMut = useMutation({
    mutationFn: () => api.post(`/permits/auto-detect`, { project_id: projectId, commit: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['permits'] });
      qc.invalidateQueries({ queryKey: ['permits-all'] });
    },
  });

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Permits</h1>
          <div className="page__subtitle">
            Every permit, anchored to as-built · {project ? `${project.project_code} · ${project.project_name}` : 'Loading…'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 13, minWidth: 280 }}
          >
            {selectableProjects.map(p => (
              <option key={p.id} value={p.id}>{p.project_code} — {p.project_name} ({p.state ?? '—'})</option>
            ))}
          </select>
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
            {(['split', 'map', 'list'] as ViewMode[]).map(m => (
              <button key={m} onClick={() => setViewMode(m)} style={{
                padding: '6px 12px',
                background: viewMode === m ? '#0f172a' : '#fff',
                color: viewMode === m ? '#fff' : '#0f172a',
                border: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                textTransform: 'capitalize',
              }}>{m}</button>
            ))}
          </div>
          <button
            onClick={() => detectMut.mutate()}
            disabled={detectMut.isPending}
            title="Re-run auto-detection against current as-built state. Existing permits are deduped."
            style={{
              padding: '8px 14px', background: '#fff', color: '#0f172a',
              border: '1px solid #cbd5e1', borderRadius: 4,
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {detectMut.isPending ? 'Detecting…' : '↻ Re-detect'}
          </button>
          <button
            onClick={() => setAddOpen(true)}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
              color: '#fff', border: 0, borderRadius: 4,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            + Add permit
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 16 }}>
        <Kpi label="Total" value={String(kpis.total)} accent="#0f172a" />
        <Kpi label="Needed" value={String(kpis.needed)} accent="#ef4444" sub={kpis.needed > 0 ? 'Action required' : ''} />
        <Kpi label="In flight" value={String(kpis.inFlight)} accent="#f59e0b" sub="Applied or in review" />
        <Kpi label="Issued" value={String(kpis.issued)} accent="#10b981" sub="Approved or issued" />
        <Kpi label="Denied" value={String(kpis.denied)} accent="#7f1d1d" />
        <Kpi label="Expiring < 30d" value={String(kpis.expiringIn30)} accent={kpis.expiringIn30 > 0 ? '#f59e0b' : '#10b981'} />
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        <FilterChip label={`All (${kpis.total})`} active={bucketFilter === null && statusFilter === null}
          onClick={() => { setBucketFilter(null); setStatusFilter(null); }} />
        {/* Status filters */}
        {(['needed', 'applied', 'in_review', 'issued', 'denied'] as const).map(s => {
          const count = kpis.byStatus[s] ?? 0;
          if (count === 0 && statusFilter !== s) return null;
          return <FilterChip
            key={s}
            label={`${STATUS_META[s].label} (${count})`}
            color={STATUS_META[s].fg}
            active={statusFilter === s}
            onClick={() => setStatusFilter(statusFilter === s ? null : s)}
          />;
        })}
        {/* Divider */}
        {Object.keys(kpis.byBucket).length > 0 && (
          <span style={{ width: 1, height: 20, background: '#cbd5e1', alignSelf: 'center', margin: '0 4px' }} />
        )}
        {/* Bucket filters */}
        {Object.entries(kpis.byBucket).map(([bucket, count]) => (
          <FilterChip
            key={bucket}
            label={`${BUCKET_META[bucket]?.icon ?? ''} ${BUCKET_META[bucket]?.label ?? bucket} (${count})`}
            color={BUCKET_META[bucket]?.color}
            active={bucketFilter === bucket}
            onClick={() => setBucketFilter(bucketFilter === bucket ? null : bucket)}
          />
        ))}
      </div>

      {/* Map */}
      {viewMode !== 'list' && (
        <div style={{ marginBottom: 16 }}>
          <PermitsMap
            projectId={projectId}
            permits={permits}
            height={viewMode === 'map' ? 'calc(100vh - 280px)' : 460}
            onPermitClick={setSelectedPermit}
          />
        </div>
      )}

      {/* List */}
      {viewMode !== 'map' && (
        <div className="card">
          <div className="card__header">
            <span>Permits · {permits.length} {bucketFilter || statusFilter ? '(filtered)' : ''}</span>
          </div>
          <div className="card__body" style={{ padding: 0 }}>
            {permits.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
                {all.length === 0
                  ? <>No permits for this project. Click <b>↻ Re-detect</b> to auto-create the BEAD-required permits and detect permits from as-built data.</>
                  : 'No permits match the current filters.'}
              </div>
            )}
            {permits.map(p => (
              <PermitRow key={p.id} permit={p} onClick={() => setSelectedPermit(p)} />
            ))}
          </div>
        </div>
      )}

      {addOpen && (
        <AddPermitModal
          projectId={projectId}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['permits'] });
            qc.invalidateQueries({ queryKey: ['permits-all'] });
            setAddOpen(false);
          }}
        />
      )}

      {selectedPermit && (
        <PermitDetailDrawer
          permitId={selectedPermit.id}
          onClose={() => setSelectedPermit(null)}
          onUpdated={() => {
            qc.invalidateQueries({ queryKey: ['permits'] });
            qc.invalidateQueries({ queryKey: ['permits-all'] });
          }}
        />
      )}
    </>
  );
}

function Kpi({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="card" style={{ borderTop: accent ? `3px solid ${accent}` : undefined }}>
      <div className="card__body" style={{ padding: 14 }}>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4, color: '#0f172a' }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function FilterChip({ label, active, color, onClick }: { label: string; active: boolean; color?: string; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999,
      border: `1px solid ${active ? '#0f172a' : 'var(--border)'}`,
      background: active ? '#0f172a' : '#fff',
      color: active ? '#fff' : (color ?? '#475569'),
      cursor: 'pointer',
    }}>{label}</button>
  );
}

function PermitRow({ permit, onClick }: { permit: Permit; onClick: () => void }) {
  const bucket = BUCKET_META[permit.bucket] ?? BUCKET_META.other;
  const now = new Date();
  const daysToExpiry = permit.expires_at ? Math.round((new Date(permit.expires_at).getTime() - now.getTime()) / 86400000) : null;
  const expiresSoon = daysToExpiry != null && daysToExpiry >= 0 && daysToExpiry <= 30;
  return (
    <div
      onClick={onClick}
      onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
      style={{
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 12,
        borderLeft: `4px solid ${bucket.color}`,
        cursor: 'pointer', transition: 'background 0.12s',
        background: 'white',
      }}
    >
      <div style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{bucket.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{KIND_LABELS[permit.kind] ?? permit.kind}</span>
          {permit.feature_name && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>· {permit.feature_name}</span>}
          {Array.isArray((permit as any).grants) && (permit as any).grants.map((g: string) => (
            <span key={g} title="Grant program(s) this permit supports" style={{ fontSize: 9, fontWeight: 700, background:'#eef2ff', color:'#4338ca', padding:'1px 6px', borderRadius: 8 }}>{g}</span>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
          {permit.agency_short_name || permit.agency_name || (permit.feature_owner ?? 'No agency')} ·
          {' '}{permit.jurisdiction ?? 'jurisdiction TBD'}
          {permit.permit_number && <> · #{permit.permit_number}</>}
          {expiresSoon && <span style={{ color: '#b45309', marginLeft: 6, fontWeight: 600 }}>· expires in {daysToExpiry}d</span>}
          {(permit as any).crossings_count > 0 && (
            <span style={{ color: '#1e3a8a', marginLeft: 6 }}>· {(permit as any).crossings_count} crossings</span>
          )}
        </div>
      </div>
      {/* "Blocks construction" badge — non-issued permit covering segments
          that aren't yet built reads as a real gate, not just paperwork. */}
      {!['issued', 'approved', 'waived', 'n_a'].includes(permit.status) && (
        <span style={{
          padding: '2px 8px', fontSize: 10, fontWeight: 700,
          background: '#fef3c7', color: '#92400e', borderRadius: 999,
          border: '1px solid #fbbf24', marginRight: 6,
        }} title="This permit must be issued before its covered segments can start construction.">
          🚧 BLOCKS BUILD
        </span>
      )}
      <StatusBadge status={permit.status} />
    </div>
  );
}
